import React, { useEffect, useRef, useState } from 'react';
import { Box, VStack, useTheme, FormControl, Spinner, Text } from 'native-base';
import ReactQuill, { Quill } from 'react-quill';
const Link = Quill.import('formats/link');
import 'react-quill/dist/quill.snow.css';
import * as Mention from 'quill-mention';
import ChooseCategory from '../../topics/ChooseCategory';
import { getFriendlyOrDiscourseUsername } from '../../../utils/helpers';
import 'quill-mention/dist/quill.mention.css';
import { useEnvState } from '../../../../context/env';
import { topicsAPI } from '../../../../context/topics';
import { useAuthState } from '../../../../context/auth';
import { searchAPI } from '../../../../context/search';
import { EDITOR_HEIGHT, EDITOR_MAX_HEIGHT, AVATAR_SIZE, LANGUAGES, MENTION_ICON, RAW_MIN_LENGTH, Z_INDEX } from '../../../Constants';
import './RichTextBox.css'; // still needed for renderItem method to override default styling
import PostModalFooter from './PostModalFooter';
class LinkWithPrefix extends Link {
  static sanitize(url) {
    let value = super.sanitize(url);
    if (value) {
      for (let i = 0; i < this.PROTOCOL_WHITELIST.length; i++) if (value.startsWith(this.PROTOCOL_WHITELIST[i])) return value;
      return `https://${value}`;
    }
    return value;
  }
}
Quill.register(LinkWithPrefix);
Quill.register('modules/mentions', Mention);
const formats = ['header', 'bold', 'italic', 'strike', 'mention', 'blockquote', 'list', 'bullet', 'link', 'image'];
const punctuationMarks = ['.', ',', '?', '!', ':', ';', "'"]; // test value for possible use in the future

// import icons list from quill
// and set icon for mention
const icons = Quill.import('ui/icons');
icons.mention = MENTION_ICON;
const RichTextBox = ({
  topic,
  id,
  key,
  selectedPostCategoryId,
  setSelectedPostCategoryId,
  topicId,
  existingContent,
  handlePost,
  setShowModal,
  changeCategoriesAllowed = true
}) => {
  const theme = useTheme();
  const {
    colors
  } = theme;
  const authState = useAuthState();
  const envState = useEnvState();
  const {
    base_url,
    product
  } = envState;
  const {
    api_key
  } = authState;
  const quillRef = useRef();
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const isNewTopicModal = id === 'newTopic';
  const isNewReplyModal = id === 'newReply';
  const isEditModal = id === 'edit';
  const [label, setLabel] = useState('');
  const [categoryErrorMsg, setCategoryErrorMsg] = useState('');
  const [contentErrorMsg, setContentErrorMsg] = useState(selectedPostCategoryId ? true : false);
  const initialData = isNewTopicModal ? {
    raw: '',
    title: ''
  } : isNewReplyModal ? {
    raw: '',
    topic_id: topicId ? topicId : undefined
  } : isEditModal ? {
    raw: existingContent ? existingContent.raw : ''
  } : {};
  const [data, setData] = useState(initialData);
  const [showSpinner, setShowSpinner] = useState(false);
  const [isContentValid, setIsContentValid] = useState(false);
  const [isCategoryValid, setIsCategoryValid] = useState(false);
  const [isDataValid, setIsDataValid] = useState(false);
  const isNewOrEditReply = id === 'newReply' || id === 'edit' && !topic;
  // add tooltip (title attribute) to mention button
  useEffect(() => {
    const forceDelayValue = 1;

    // update with setAttribute gets overridden by editor re-render that follows almost immediately
    setTimeout(() => {
      const toolbarEl = document.querySelector('.ql-toolbar');
      const mentionButtonEl = toolbarEl ? toolbarEl.querySelector('button.ql-mention') : null;
      if (mentionButtonEl) {
        mentionButtonEl.setAttribute('title', LANGUAGES.TOOLTIP.mention);
      }
    }, forceDelayValue);
  }, [data.raw]); // added as a dependency to keep the tooltip

  // disable react-quill spell check
  useEffect(() => {
    const editor = quillRef.current && quillRef.current.editor;
    if (editor) {
      editor.root.setAttribute('spellcheck', false);
    }
  }, []);
  useEffect(() => {
    if (!data.raw) {
      setMentionedUsers([]);
    }
  }, [data.raw]);
  useEffect(() => {
    if (isNewTopicModal) {
      setLabel('Contents');
    } else if (isNewReplyModal) {
      setLabel('Write a reply');
    } else if (isEditModal) {
      setLabel('Edit post');
    }
  }, [id]);
  useEffect(() => {
    if (selectedPostCategoryId) {
      setIsCategoryValid(true);
    } else {
      setIsCategoryValid(false);
    }
    if (!isCategoryValid) {
      setCategoryErrorMsg('Category is required');
    }
  }, [selectedPostCategoryId, isCategoryValid]);
  useEffect(() => {
    if (data.raw && data.raw.length > RAW_MIN_LENGTH) {
      setIsContentValid(true);
    } else {
      setIsContentValid(false);
    }
    if (!isContentValid) {
      setContentErrorMsg(`Please enter at least ${RAW_MIN_LENGTH} characters.`);
    }
  }, [data.raw, isContentValid]);

  // data validation
  useEffect(() => {
    if (isContentValid) {
      if (isNewTopicModal && isCategoryValid || isNewReplyModal && topicId || isEditModal) {
        setIsDataValid(true);
      }
    } else {
      setIsDataValid(false);
    }
  }, [isContentValid, isCategoryValid, isNewTopicModal, isNewReplyModal, isEditModal, topicId]);
  const getUsersList = async searchTerm => {
    const response = await searchAPI.searchUser(base_url, api_key, searchTerm, product);
    if (response && response.users && response) {
      const {
        users
      } = response;
      const filteredUsers = users.filter(user => user.name.toLowerCase().includes(searchTerm.toLowerCase()));
      return filteredUsers;
    }
  };
  const openMenu = () => {
    if (quillRef && quillRef.current) {
      const editor = quillRef.current.getEditor();
      if (editor && editor.insertText && editor.getSelection && editor.setSelection) {
        const cursorPosition = editor.getSelection().index;
        editor.insertText(cursorPosition, '@');
        editor.setSelection(cursorPosition + 1);
      }
    }
  };
  const handleImage = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.style.display = 'none';
    document.body.appendChild(input);
    input.click();
    input.onchange = async () => {
      setShowSpinner(true);
      const file = input && input.files ? input.files[0] : null;
      if (file) {
        const formData = new FormData();
        formData.append('upload_type', 'composer');
        formData.append('pasted', undefined);
        formData.append('name', file.name);
        formData.append('sha1_checksum', '');
        formData.append('type', file.type);
        formData.append('file', file);
        const res = await topicsAPI.uploadFile(base_url, api_key, formData);
        if (res) {
          const imageUrl = 'https:' + res.url.replaceAll('"', '');
          if (quillRef && quillRef.current && quillRef.current.getEditor) {
            const editor = quillRef.current.getEditor();
            editor.focus();
            const range = editor.getSelection();
            if (!range) {
              const length = editor.getLength();
              editor.setSelection(length, length);
            }
            editor.insertEmbed(range.index, 'image', imageUrl);
            setShowSpinner(false);
          }
        } else {
          throw new Error('Uploading file failed');
        }
      }
      document.body.removeChild(input);
    };
  };
  const modules = React.useMemo(() => ({
    mention: {
      allowedChars: /^[a-z\sÅÄÖåäö]*$/i,
      mentionDenotationChars: ['@'],
      // default
      dataAttributes: ['value', 'name', 'username', 'avatar_template', 'moderator'],
      defaultMenuOrientation: 'bottom',
      minChars: 1,
      // get users list only when search term exists
      spaceAfterInsert: false,
      // set to false to prevent unexpected behaviour, as it's true by default
      renderItem: item => {
        var _item$avatar_template;
        // layout for each user item
        const isModerator = item.moderator && item.moderator === true ? true : false;
        const friendlyUsername = isModerator ? '<strong>' + item.name + '.</strong>' : item.name;
        return `
          <div class='mention-list-item'>
          <img src='${base_url + (item === null || item === void 0 || (_item$avatar_template = item.avatar_template) === null || _item$avatar_template === void 0 ? void 0 : _item$avatar_template.replace(/\{size\}/, AVATAR_SIZE))}'
            class='mention-list-avatar'
          />
            <p>${isModerator ? friendlyUsername + ' <span>(M)</span>' : friendlyUsername}</p>
          </div>
          `;
      },
      source: async function (searchTerm, renderList) {
        const users = await getUsersList(searchTerm);
        renderList(users);
      },
      onSelect: (item, insertItem) => {
        const selectedUser = {
          username: getFriendlyOrDiscourseUsername(item, 'discourse'),
          value: getFriendlyOrDiscourseUsername(item, 'friendly'),
          // what users see in editor
          denotationChar: '@',
          avatar_template: item.avatar_template
        };
        insertItem(selectedUser);

        // to prevent editor from losing focus on select
        setMentionedUsers(currentList => [...currentList, selectedUser.value]);
        const editorRef = quillRef.current;
        if (editorRef) {
          const contents = editorRef.getEditorContents();
          const editor = editorRef.getEditor();
          if (editor) {
            // manually adding a space after mention blot
            const extraSpace = ' ';
            const indexOfLastClosingPTag = contents.lastIndexOf('</p>'); // there's only one p tag, but just to make sure
            const newContentsWithSpace = contents.substring(0, indexOfLastClosingPTag) + extraSpace + contents.substr(indexOfLastClosingPTag);
            editorRef.setEditorContents(editor, newContentsWithSpace);

            // place the cursor at the end of the extra space
            editor.setSelection(newContentsWithSpace.length, newContentsWithSpace.length);
          }
        }
      }
    },
    toolbar: {
      container: [[{
        header: [1, 2, false]
      }], ['bold', 'italic', 'strike', 'blockquote', 'mention'], [{
        list: 'ordered'
      }, {
        list: 'bullet'
      }], ['link', 'image']],
      handlers: {
        mention: openMenu,
        image: handleImage
      }
    }
  }), []);
  const handleOnChange = contents => {
    if (contents) {
      // update new topic data
      if (isNewTopicModal) {
        const beginningPTagLength = '<p>'.length; // to exclude <p> at the beginning
        setData({
          raw: contents,
          title: contents === null || contents === void 0 ? void 0 : contents.substring(beginningPTagLength, RAW_MIN_LENGTH) // use contents to generate title
        });
        // update new reply data
      } else if (isNewReplyModal) {
        setData({
          ...data,
          // keep topic_id
          raw: contents
        });
        // update edit data
      } else if (isEditModal) {
        setData({
          raw: contents
        });
      }
    }
  };
  useEffect(() => {
    var _quillRef$current;
    // this useEffect ensure the tooltip initially pops up within the editor box
    const quill = (_quillRef$current = quillRef.current) === null || _quillRef$current === void 0 ? void 0 : _quillRef$current.getEditor();
    if (!quill) return;
    quill.on('selection-change', range => {
      if (range && range.length > 0) {
        const tooltip = quill.theme.tooltip;
        if (tooltip && tooltip.root) {
          setTimeout(() => {
            const bounds = quill.getBounds(range.index);
            const editorContainer = quill.root.parentElement.getBoundingClientRect(); // Get editor position
            const editorBounds = quill.root.getBoundingClientRect();
            tooltip.root.style.top = `${editorContainer.top}px`;
            tooltip.root.style.marginTop = '0px';
          }, 0);
        }
      }
    });
  }, []);
  useEffect(() => {
    var _quillRef$current2;
    // This useEffect ensures the tooltip STAYS within the bounds of the editor box during scroll
    const quill = (_quillRef$current2 = quillRef.current) === null || _quillRef$current2 === void 0 ? void 0 : _quillRef$current2.getEditor();
    if (!quill) return;
    const editorScrollContainer = document.querySelector('.ql-editor');
    if (!editorScrollContainer) return;
    const updateTooltipPosition = () => {
      const tooltip = quill.theme.tooltip;
      if (!tooltip || !tooltip.root) return;
      const editorContainer = quill.root.parentElement.getBoundingClientRect();
      tooltip.root.style.marginTop = '0px';
    };
    editorScrollContainer.addEventListener('scroll', updateTooltipPosition);
    return () => editorScrollContainer.removeEventListener('scroll', updateTooltipPosition);
  }, []);
  const clearContents = () => {
    if (quillRef && quillRef.current) {
      const contents = quillRef.current.getEditorContents();
      if (contents) {
        quillRef.current.getEditor().setContents('');
        setIsContentValid(false);
      }
    }
  };
  return /*#__PURE__*/React.createElement(Box, isNewOrEditReply && {
    rounded: 'md',
    borderWidth: 1,
    borderColor: colors.reply.border,
    position: 'relative'
  }, /*#__PURE__*/React.createElement(VStack, null, showSpinner && /*#__PURE__*/React.createElement(Box, {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    style: {
      opacity: 0.7,
      position: 'absolute',
      left: 0,
      top: 0,
      'background-color': 'white'
    },
    zIndex: Z_INDEX.top,
    h: '150%',
    w: '100%'
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Spinner, null), /*#__PURE__*/React.createElement(Text, null, "Uploading Image"))), changeCategoriesAllowed && isNewTopicModal && /*#__PURE__*/React.createElement(FormControl, {
    isRequired: true,
    isInvalid: !isCategoryValid
  }, /*#__PURE__*/React.createElement(FormControl.Label, null, "Select Category"), /*#__PURE__*/React.createElement(ChooseCategory, {
    key: "chooseCategory",
    selectedPostCategoryId: selectedPostCategoryId,
    setSelectedPostCategoryId: setSelectedPostCategoryId,
    setIsCategoryValid: setIsCategoryValid
  }), !isCategoryValid && /*#__PURE__*/React.createElement(FormControl.ErrorMessage, null, categoryErrorMsg)), /*#__PURE__*/React.createElement(FormControl, {
    isRequired: true,
    isInvalid: !isContentValid
  }, !isNewOrEditReply && /*#__PURE__*/React.createElement(FormControl.Label, null, label), /*#__PURE__*/React.createElement(ReactQuill, {
    ref: quillRef,
    defaultValue: data.raw // for EditBox to show the current "cooked" value
    ,
    onChange: handleOnChange,
    placeholder: LANGUAGES.PLACEHOLDERS.EDITOR,
    theme: 'snow',
    modules: modules,
    formats: formats,
    style: {
      height: EDITOR_HEIGHT,
      maxHeight: EDITOR_MAX_HEIGHT
    },
    id: id,
    key: key,
    bounds: topic && document.querySelector(`#topic-${topic.id}`)
  }), !isContentValid && !isNewOrEditReply && /*#__PURE__*/React.createElement(FormControl.ErrorMessage, {
    mt: 12
  }, contentErrorMsg)), !isNewOrEditReply && /*#__PURE__*/React.createElement(Text, {
    onPress: clearContents,
    w: "max-content",
    position: "absolute",
    top: "0",
    right: "0",
    color: colors.postModal.clearContents,
    _hover: {
      cursor: 'pointer'
    }
  }, "Clear"), /*#__PURE__*/React.createElement(Text, {
    mt: isContentValid ? 12 : null
  }, ' '), /*#__PURE__*/React.createElement(PostModalFooter, {
    topic: topic,
    isTypeTopic: isNewTopicModal,
    id: id,
    key: key,
    selectedPostCategoryId: selectedPostCategoryId,
    data: data,
    handlePost: handlePost,
    setShowModal: setShowModal,
    isValid: isDataValid,
    handleImage: handleImage
  })));
};
export default RichTextBox;
//# sourceMappingURL=RichTextBox.js.map